import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CalendarBlank, Plus, Clock, Users, Phone, Trash, Check, X, Seat } from "@phosphor-icons/react";
import { format } from "date-fns";

const statusColors = {
  confirmed: "bg-blue-100 text-blue-800",
  seated: "bg-green-100 text-green-800",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
  no_show: "bg-yellow-100 text-yellow-800",
};

const ReservationsPage = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reservations, setReservations] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    guest_name: "",
    guest_phone: "",
    guest_email: "",
    party_size: 2,
    table_id: "auto",
    reservation_time: "",
    duration_minutes: 90,
    notes: "",
  });
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState("");
  const [showCustResults, setShowCustResults] = useState(false);

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resRes, slotsRes, tablesRes, custRes] = await Promise.all([
        axios.get(`${API}/reservations?date=${dateStr}`),
        axios.get(`${API}/reservations/slots/${dateStr}`),
        axios.get(`${API}/tables`),
        axios.get(`${API}/customers`).catch(() => ({ data: [] })),
      ]);
      setReservations(resRes.data);
      setTimeSlots(slotsRes.data);
      setTables(tablesRes.data);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error("Failed to load reservations:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f,
      guest_name: c.name || "",
      guest_phone: c.phone || "",
      guest_email: c.email || "",
    }));
    setCustQuery(c.name || "");
    setShowCustResults(false);
  };

  const filteredCustomers = (() => {
    const q = custQuery.trim().toLowerCase();
    if (!q) return [];
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    ).slice(0, 6);
  })();

  const handleCreate = async () => {
    if (!form.guest_name || !form.guest_phone || !form.reservation_time) {
      toast.error("Please fill in required fields");
      return;
    }
    try {
      await axios.post(`${API}/reservations`, {
        ...form,
        reservation_date: dateStr,
        table_id: form.table_id === "auto" ? null : form.table_id,
      });
      toast.success("Reservation created");
      setShowDialog(false);
      setForm({ guest_name: "", guest_phone: "", guest_email: "", party_size: 2, table_id: "auto", reservation_time: "", duration_minutes: 90, notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create reservation");
    }
  };

  const handleStatusUpdate = async (resId, newStatus) => {
    try {
      await axios.put(`${API}/reservations/${resId}`, { status: newStatus });
      toast.success(`Reservation ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update reservation");
    }
  };

  const handleDelete = async (resId) => {
    try {
      await axios.delete(`${API}/reservations/${resId}`);
      toast.success("Reservation deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete reservation");
    }
  };

  const confirmedCount = reservations.filter(r => r.status === "confirmed").length;
  const seatedCount = reservations.filter(r => r.status === "seated").length;

  return (
    <div className="min-h-screen bg-background" data-testid="reservations-page">
      <Sidebar />

      <main className="main-content p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-medium text-foreground">Reservations</h1>
            <p className="text-muted-foreground">Manage table reservations</p>
          </div>

          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="res-date-picker">
                  <CalendarBlank size={18} />
                  {format(selectedDate, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} initialFocus />
              </PopoverContent>
            </Popover>

            <Button className="gap-2" onClick={() => setShowDialog(true)} data-testid="new-reservation-btn">
              <Plus size={18} />
              New Reservation
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CalendarBlank size={24} className="text-blue-600" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Today</p>
                <p className="font-mono text-2xl font-medium">{reservations.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Check size={24} className="text-green-600" weight="bold" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Confirmed</p>
                <p className="font-mono text-2xl font-medium">{confirmedCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Seat size={24} className="text-primary" weight="fill" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Seated</p>
                <p className="font-mono text-2xl font-medium">{seatedCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Time Slots Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Available Time Slots</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot.time}
                  onClick={() => {
                    if (slot.is_available) {
                      setForm(prev => ({ ...prev, reservation_time: slot.time }));
                      setShowDialog(true);
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-mono transition-colors ${
                    slot.is_available
                      ? "bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer border border-green-200"
                      : "bg-red-50 text-red-400 cursor-not-allowed border border-red-100"
                  }`}
                  data-testid={`slot-${slot.time}`}
                  disabled={!slot.is_available}
                >
                  {slot.time}
                  <span className="text-[10px] block">{slot.available_tables} tables</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reservations List */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Reservations for {format(selectedDate, "MMMM d, yyyy")}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : reservations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarBlank size={48} className="mx-auto mb-2 opacity-30" />
                <p>No reservations for this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {reservations.map((res) => (
                  <div
                    key={res.id}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl border border-border hover:border-primary/20 transition-colors"
                    data-testid={`reservation-${res.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="font-mono text-lg font-bold text-primary">{res.reservation_time}</p>
                        <p className="text-[10px] text-muted-foreground">{res.duration_minutes}min</p>
                      </div>
                      <div>
                        <p className="font-medium">{res.guest_name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Users size={14} />{res.party_size}</span>
                          <span className="flex items-center gap-1"><Phone size={14} />{res.guest_phone}</span>
                          {res.table_name && <span className="flex items-center gap-1"><Seat size={14} />{res.table_name}</span>}
                        </div>
                        {res.notes && <p className="text-xs text-muted-foreground italic mt-1">{res.notes}</p>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge className={`${statusColors[res.status]} border-0 capitalize`}>{res.status.replace("_", " ")}</Badge>

                      {res.status === "confirmed" && (
                        <Button size="sm" variant="outline" className="gap-1 text-green-600" onClick={() => handleStatusUpdate(res.id, "seated")} data-testid={`seat-${res.id}`}>
                          <Seat size={14} /> Seat
                        </Button>
                      )}
                      {res.status === "seated" && (
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => handleStatusUpdate(res.id, "completed")} data-testid={`complete-${res.id}`}>
                          <Check size={14} /> Complete
                        </Button>
                      )}
                      {res.status === "confirmed" && (
                        <>
                          <Button size="sm" variant="ghost" className="text-yellow-600" onClick={() => handleStatusUpdate(res.id, "no_show")} data-testid={`noshow-${res.id}`}>
                            No Show
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleStatusUpdate(res.id, "cancelled")} data-testid={`cancel-${res.id}`}>
                            <X size={14} />
                          </Button>
                        </>
                      )}
                      <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => handleDelete(res.id)} data-testid={`delete-res-${res.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* New Reservation Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setCustQuery(""); setShowCustResults(false); } }}>
        <DialogContent className="sm:max-w-[480px] max-h-[85vh] overflow-y-auto" data-testid="reservation-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">New Reservation</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Customer Lookup */}
            <div className="space-y-2 relative">
              <Label>Look up Customer (optional)</Label>
              <Input
                placeholder="Search by name, phone, or email..."
                value={custQuery}
                onChange={(e) => { setCustQuery(e.target.value); setShowCustResults(true); }}
                onFocus={() => setShowCustResults(true)}
                data-testid="res-customer-lookup"
              />
              {showCustResults && filteredCustomers.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="customer-lookup-results">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary transition-colors border-b border-border last:border-0"
                      data-testid={`customer-lookup-${c.id}`}
                    >
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.phone || ""}{c.phone && c.email ? " · " : ""}{c.email || ""}</p>
                    </button>
                  ))}
                </div>
              )}
              {showCustResults && custQuery.trim() && filteredCustomers.length === 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-card border border-border rounded-lg shadow-lg px-3 py-2 text-xs text-muted-foreground">
                  No matching customers. Fill the form below to enter manually.
                </div>
              )}
              <p className="text-xs text-muted-foreground">Pick an existing customer to auto-fill name, phone, and email — or just type new details below.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input placeholder="John Smith" value={form.guest_name} onChange={(e) => setForm(f => ({ ...f, guest_name: e.target.value }))} data-testid="res-guest-name" />
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input placeholder="(555) 123-4567" value={form.guest_phone} onChange={(e) => setForm(f => ({ ...f, guest_phone: e.target.value }))} data-testid="res-guest-phone" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input placeholder="email@example.com" value={form.guest_email} onChange={(e) => setForm(f => ({ ...f, guest_email: e.target.value }))} data-testid="res-guest-email" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Party Size *</Label>
                <Select value={String(form.party_size)} onValueChange={(v) => setForm(f => ({ ...f, party_size: parseInt(v) }))}>
                  <SelectTrigger data-testid="res-party-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "guest" : "guests"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <Select value={form.reservation_time} onValueChange={(v) => setForm(f => ({ ...f, reservation_time: v }))}>
                  <SelectTrigger data-testid="res-time"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {timeSlots.filter(s => s.is_available).map(s => (
                      <SelectItem key={s.time} value={s.time}>{s.time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Duration</Label>
                <Select value={String(form.duration_minutes)} onValueChange={(v) => setForm(f => ({ ...f, duration_minutes: parseInt(v) }))}>
                  <SelectTrigger data-testid="res-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assign Table (optional)</Label>
              <Select value={form.table_id} onValueChange={(v) => setForm(f => ({ ...f, table_id: v }))}>
                <SelectTrigger data-testid="res-table"><SelectValue placeholder="Auto-assign" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-assign</SelectItem>
                  {tables.filter(t => t.status === "available").map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.seats} seats)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea placeholder="Special requests, allergies, etc." value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="res-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="save-reservation-btn">Create Reservation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationsPage;
